<template>
  <div v-if="announcement || modal" :class="{'is-live-event': liveEvent, 'announcement notification is-secondary': !showModal}" class="has-text-centered">
    <template v-if="liveEvent">
      <p>Your event <span class="shortcode">{{ liveEvent.shortcode }}</span> is currently active</p>
      <div @click="startEndEvent(liveEvent.id)" class="button is-white is-warning" data-cy="end-event-button">
        End Event
      </div>
      <n-link :to="'/event/' + liveEvent.shortcode" class="button is-black is-inverted">
        Go to Event &rarr;
      </n-link>
    </template>
    <template v-else-if="passExpiring">
      <p>Your premium pass is <b>expiring in {{ pass.daysLeft }} days</b>! Get a discount on a longer plan:</p>
      <div @click.prevent="startUpgrade()" class="button is-secondary is-inverted">
        Click here to extend 🔥
      </div>
    </template>

    <template v-else-if="passExpired">
      <p>Your premium pass has expired! Upgrade now to continue using pro features:</p>
      <div @click.prevent="startUpgrade()" class="button is-secondary is-inverted">
        Click here to upgrade 🔥
      </div>
    </template>

    <template v-else-if="newPass">
      <p><span class="capitalize">🎫 {{ pass.bestPass }}</span> pass is active!&nbsp; <b>You can host up to {{ playerLimit }} players for the next {{ pass.daysLeft }} days</b>.&nbsp; <a @click.prevent="startUpgrade()">Click here to extend with discount</a></p>
    </template>
    <template v-else-if="unconfirmedEmail && !showModal">
      <p><span class="icon" style="position: relative; top: -1px; left: -4px;">📩</span><b>Confirm your email address to use all features.</b><br> Check spam and add us to contacts if you use Gmail or Google Workspace</p>
    </template>
    <template v-else-if="unconfirmedEmail && showModal">
      <b-modal :active="showModal" :width="550" :can-cancel="false" :trap-focus="true">
        <div class="confirm-modal has-text-centered">
          <div class="content">
            <div class="img-container">
              <img src="/email-confirm.png" alt="Confirm Email" style="max-width: 100%; height: auto;">
            </div>
            <h2 class="title is-3"><b>Confirm your email to get started</b></h2>
            <p><span class="wiggly-underline">Check your spam folder</span> and if you use Gmail or Google Workspace mark us as <img style="position:relative; top: 5px; right: 1px;" src="/gmail-important-filled.png">important. We use email for billing management, password reset, event reports, and other important notices. Make sure you don't miss something!</p>
            <p @click="tryCloseEmailModal()" :disabled="!canCloseModal" class="button is-text">close this window <span v-if="waitTime > 0">&nbsp;({{ waitTime }}s)</span></p>
          </div>
        </div>
      </b-modal>
    </template>
  </div>
</template>

<script>
import { getUnixTime, parseISO } from 'date-fns'
import EndEvent from '~/components/mixins/EndEvent'
import ExtendPass from '~/components/modals/ExtendPass'
import UpgradeAccount from '~/components/modals/UpgradeAccount'

export default {
  name: 'Announcements',
  mixins: [
    EndEvent
  ],
  data () {
    return {
      showModal: false,
      canCloseModal: false,
      waitTime: 60,
      nextWaitTimes: 20,
      countdownInterval: null,
      timerStarted: false
    }
  },
  computed: {
    announcement () {
      return this.liveEvent ||
             this.passExpired ||
             (this.isPassActive && (this.newPass || this.passExpiring)) ||
             (this.unconfirmedEmail && !this.showModal)
    },
    modal () {
      return this.unconfirmedEmail && this.showModal
    },
    user () {
      return this.$store.state.user
    },
    userMeta () {
      return this.user.metadata || {}
    },
    playerLimit () {
      return this.$store.getters.playerLimit
    },
    pass () {
      return this.$store.getters.eventPasses
    },
    isPassActive () {
      return this.$store.getters.isPassActive
    },
    newPass () {
      return this.isPassActive && this.pass.daysLeft >= 10
    },
    passExpiring () {
      return this.isPassActive && this.pass.daysLeft < 10 && this.pass.daysLeft > -1
    },
    passExpired () {
      return this.pass.hasPass && this.pass.daysLeft < 0 && this.pass.daysLeft < -10
    },
    userAge () {
      return getUnixTime(new Date()) - getUnixTime(parseISO(this.user.createdAt))
    },
    recentUser () {
      if (!this.user.id) { return false }
      return this.userAge < 60 * 60 * 24 * 14 // registered in the last 30 days
    },
    confirmationEmailSent () {
      return this.$store.confirmationEmailSent || this.userMeta.confirmationEmailSent === true
    },
    emailModalShownRecently () {
      const modalShown = this.$store.state.confirmEmailModalShown
      if (!modalShown) { return false }
      return getUnixTime(new Date()) - modalShown < 60 * 60 * 4 // shown in the last 4 hours
    },
    unconfirmedEmail () {
      return this.recentUser &&
             this.confirmationEmailSent &&
             this.user.emailVerified === false
    }
  },
  watch: {
    unconfirmedEmail (value) {
      if (value === true && this.timerStarted === false) {
        this.startModalTimer()
      }
    }
  },
  mounted () {
    this.checkPostEventModal()

    if (this.unconfirmedEmail && this.timerStarted === false) {
      this.startModalTimer()
    }
  },
  methods: {
    startModalTimer () {
      if (this.emailModalShownRecently) { return }

      this.$store.commit('confirmEmailModalShown')
      this.showModal = true
      this.posthogCapture('register:confirm_email_modal_open', { $set: { email_confirmed: false } })
      this.timerStarted = true
      if (this.userAge > 60 * 5) { this.waitTime = this.nextWaitTimes }
      this.countdownInterval = setInterval(() => {
        if (this.waitTime <= 1) {
          clearInterval(this.countdownInterval)
          this.canCloseModal = true
        }
        this.waitTime = this.waitTime - 1
      }, 1000)
    },
    tryCloseEmailModal () {
      if (this.canCloseModal) {
        this.showModal = false
        this.posthogCapture('register:confirm_email_modal_manual_close')
      }
    },
    startUpgrade () {
      if (this.$route.hash !== '#upgrade') {
        this.log('router not showing upgrade hash, adding it')
        this.$router.replace({ hash: 'upgrade' })
        this.trackEvent('Start Upgrade Navbar')
      }

      this.$buefy.modal.open({
        parent: this,
        fullScreen: true,
        component: this.isPassActive ? ExtendPass : UpgradeAccount,
        customClass: 'upgrade-modal',
        canCancel: false,
        trapFocus: true,
        onCancel: () => {
          this.$router.replace({ hash: '' })
        }
      })
    },
    startEndEvent (eventId) {
      this.$buefy.dialog.confirm({
        // title: 'End this event?',
        message: '<p class="m-t-10"><b>Are you sure you want to end this event?</b></p>',
        cancelText: 'Cancel',
        class: 'delete-deck',
        confirmText: `End Event`,
        onConfirm: () => {
          this.endEvent(eventId)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.wiggly-underline {
  position: relative;
  display: inline-block;
  padding-bottom: 0.2em;
  font-weight: bold;
}

.wiggly-underline::after {
  content: "";
  position: absolute;
  left: 10%;
  bottom: .1em;
  width: 80%;
  // color: #f5a0a0;
  height: 5px;
  background-image: url("data:image/svg+xml,%3Csvg width='40' height='6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0,3 Q10,0 20,3 T40,3' stroke='%23f5a0a0' stroke-width='2' fill='transparent'/%3E%3C/svg%3E");
  background-repeat: repeat-x;
  background-size: auto 100%;
  pointer-events: none;
}

.confirm-modal {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: white;
  padding: 2em;
  border-radius: 4px;

  .img-container {
    width: calc(100% + 2em);
    height: 30vh;
    background-color: #fefdf9;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: -1em -1em 0 -1em;
  }

  img {
    max-height: 30vh;
    max-width: 100%;
  }

  .emphasis {
    position: relative;
    display: inline-block;
    font-weight: bold;

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: -10px;
      left: 50%;
      width: 60px;
      height: 60px;
      background: radial-gradient(circle, transparent 40%, #00adff 40%, #00adff 42%, transparent 42%);
      background-repeat: no-repeat;
      background-size: 100% 100%;
      transform: translateX(-50%);
      z-index: -1;
      pointer-events: none;
    }

    &:after {
      top: auto;
      bottom: -10px;
      transform: translateX(-50%) rotate(180deg);
    }
  }
}
.notification.announcement {
  margin: 0;
  border-radius: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: .75rem 1.5rem;

  .button {
    margin: -.25rem 1.5em;

    & + .button {
      margin-left: -.75em;
    }
  }
}
.capitalize {
  text-transform: capitalize;
}
.shortcode {
  font-weight: bold;
  text-transform: uppercase;
}

.is-live-event.is-secondary {
  background: linear-gradient(-45deg, #e73c7e, #23a6d5, #23d5ab);
  background-size: 400% 400%;
  animation: gradient 10s ease infinite;
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

</style>
